import Splide from '@splidejs/splide';

const durationAnim = 1;
const pourcentage = 45;
const total = 360;
const strokeDasharray = ((pourcentage * total) / 100);
let heroSliderMaskId = 0;

function createMask(root, initialPath) {
    if (!initialPath || !root.querySelector('.slide-img')) {
        return null;
    }

    const maskId = `hero-slider-mask-${++heroSliderMaskId}`;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.classList.add('hero-slider-mask-defs');
    clipPath.setAttribute('id', maskId);
    clipPath.setAttribute('clipPathUnits', 'objectBoundingBox');
    path.setAttribute('d', initialPath);

    clipPath.appendChild(path);
    defs.appendChild(clipPath);
    svg.appendChild(defs);
    root.prepend(svg);

    root.querySelectorAll('.slide-img').forEach((element) => {
        element.style.clipPath = `url(#${maskId})`;
        element.style.webkitClipPath = `url(#${maskId})`;
    });

    return path;
}


export default class HeroSlider {
	static init() {
		Array.from(document.querySelectorAll('.hero-slider')).forEach(element => {
			if (!element.querySelector('.hero-slider-splide--media') || !element.querySelector('.hero-slider-splide--data')) {
				const slide = element.querySelector('.splide__slide');
				const initialPath = slide && slide.querySelector('.slide-img-shape path');
				createMask(element, initialPath && initialPath.getAttribute('d'));
				slide && slide.classList.add('is-active', 'is-visible');
				return;
			}
			new HeroSlider(element);
		});

        const isNeosBackend = document.body.classList.contains('neos-backend');
        if (isNeosBackend) {
            window.addEventListener('click', (e) => {
                if(!e.target.closest('.hero-slider')) {
                    return;
                }
                document.querySelector('.hero-slider-editable').classList.toggle('is-hidden');
            });
        }
	}

	constructor(element) {
		const options = {
			type: 'fade',
			arrows: false,
			rewind: true,
			speed: 1400 * durationAnim,
			autoplay: true,
			pauseOnHover: false,
			interval: 5000,
			throttle: 1000,
			keyboard: 'true',
			perPage: 1,
			gap: '0',
			resetProgress: false,
		};

        switch(document.documentElement.lang) {
            case 'de':
                options.i18n = {
                    prev: 'Vorheriger Artikel',
                    next: 'Nächster Artikel',
                    first: 'Gehe zum ersten Artikel',
                    last: 'Gehe zum letzten Artikel',
                    slideX: 'Gehe zu Artikel %s',
                    pageX: 'Gehe zu Seite %s',
                    play: 'Autoplay starten',
                    pause: 'Autoplay pausieren',
                }
                break;
            case 'it':
                options.i18n = {
                    prev: 'Articolo precedente',
                    next: 'Prossimo articolo',
                    first: 'Vai al primo articolo',
                    last: 'Vai all\'ultimo articolo',
                    slideX: 'Vai all\'articolo %s',
                    pageX: 'Vai alla pagina %s',
                    play: 'Avvia autoplay',
                    pause: 'Metti in pausa autoplay',
                }
                break;
            default:
                options.i18n = {
                    prev: 'Previous article',
                    next: 'Next article',
                    first: 'Go to first article',
                    last: 'Go to last article',
                    slideX: 'Go to article %s',
                    pageX: 'Go to page %s',
                    play: 'Start autoplay',
                    pause: 'Pause autoplay',
                };
        }

        this.element = element;
        this.mediaElement = element.querySelector('.hero-slider-splide--media');
        this.dataElement = element.querySelector('.hero-slider-splide--data');

        this.splide = new Splide(this.dataElement, options);
        this.mediaSplide = new Splide(this.mediaElement, {
            ...options,
            autoplay: false,
            drag: false,
            keyboard: false,
            pagination: false,
        });
        this.splide.sync(this.mediaSplide);
        this.mediaSplide.mount();
        this.splide.mount();
        this.maskPathElement = this.createMask();
        if (!this.maskPathElement) {
            this.mediaSplide.root.classList.add('hero-slider-splide--is-mask-ready');
        }
		this.splide.on('active', (e) => this.active(e));
        this.mediaSplide.on('active', (e) => this.active(e));
		this.splide.on('move', (newIndex) => this.updateMask(newIndex));
		this.splide.on('moved', (newIndex, prevIndex, destIndex) => this.movedEnd(newIndex, prevIndex, destIndex));
        this.splide.on( 'autoplay:play', () => this.dataElement.classList.add('hero-slider-splide--is-autoplaying'));
        this.splide.on( 'autoplay:pause', () => this.dataElement.classList.remove('hero-slider-splide--is-autoplaying'));
		this.splideMount();

        this.autoplayEnabled = true;
        const autoplay = this.splide.Components.Autoplay;
        const autoplayControls = [
            {selector: '.splide__play', action: () => autoplay.play(), enabled: true, label: options.i18n.play},
            {selector: '.splide__pause', action: () => autoplay.pause(), enabled: false, label: options.i18n.pause},
        ];

        autoplayControls.forEach(({selector, action, enabled, label}) => {
            const button = this.dataElement.querySelector(selector);
            button.setAttribute('aria-controls', this.splide.Components.Elements.track.id);
            button.setAttribute('aria-label', label);
            button.addEventListener('click', () => {
                action();
                this.autoplayEnabled = enabled;
            });
        });

        /** this is the accessibility view for the slider */
        this.element.querySelector('.hero-slider__list-view-button').addEventListener('click', () => this.showListView());
        this.element.querySelector('.hero-slider__slide-view-button').addEventListener('click', () => this.showSlideView());
	}

    showListView() {
        const autoplay = this.splide.Components.Autoplay;
        this.wasAutoplayingBeforeListView = this.autoplayEnabled;
        autoplay.pause();
        this.element.classList.add('hero-slider--is-list-view');
        this.element.closest('.banner-section')?.classList.add('banner-section--hero-list-view');
    }

    showSlideView() {
        this.element.classList.remove('hero-slider--is-list-view');
        this.element.closest('.banner-section')?.classList.remove('banner-section--hero-list-view');

        if (this.wasAutoplayingBeforeListView) {
            this.splide.Components.Autoplay.play();
        }
    }
	splideMount() {
		this.updatePagination(0);
        this.updateBannerColor(0);
        this.updateMask(0, true);

        const customPagers = Array.from(this.dataElement.querySelectorAll('.splide__custom__pagination li'));
		customPagers.forEach((item, index) => {
			item.addEventListener('click', (event) => {
				this.splide.go(index);
			});
		});
	}

    createMask() {
        const firstSlide = this.mediaSplide.Components.Elements.slides[0];
        const initialPath = this.getSlideMaskPath(firstSlide);

        const path = createMask(this.mediaSplide.root, initialPath);
        this.mediaSplide.root.classList.add('hero-slider-splide--is-mask-ready');

        return path;
    }

    getSlideMaskPath(slide) {
        const path = slide && slide.querySelector('.slide-img-shape path');
        return path && path.getAttribute('d');
    }

    updateMask(index, immediate = false) {
        if (!this.maskPathElement) {
            return;
        }

        const slide = this.mediaSplide.Components.Elements.slides[index];
        const path = this.getSlideMaskPath(slide);

        if (!path || this.maskPathElement.getAttribute('d') === path) {
            return;
        }

        if (immediate) {
            gsap.set(this.maskPathElement, {attr: {d: path}});
            return;
        }

        gsap.to(this.maskPathElement, {
            duration: durationAnim,
            ease: 'power4.out',
            attr: {d: path},
        });
    }

	active(e) {
		const slide = e.slide;
		const imgBox = slide.querySelectorAll('.slide-img-box');
        if (!this.maskPathElement) {
            gsap.fromTo(
                imgBox,
                {
                    clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)'}, /* xPercent: 100, autoAlpha: 1 */
                {
                    duration: durationAnim,
                    ease: 'power4.out', /* xPercent: 0, autoAlpha: 1 */
                    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
                    clearProps: 'clipPath'
                },
            );
        }
		gsap.fromTo(
			imgBox,
			{scale: 1.35},
			{duration: durationAnim, ease: 'power4.out', scale: 1},
		);
		if (this.splide.index !== 0) {
			const prevSlide = slide.previousElementSibling;
			prevSlide.style.opacity = 1;
		}
	}

	movedEnd(newIndex, prevIndex, destIndex) {
		this.updatePagination(newIndex);
        this.updateBannerColor(newIndex);
	}

    updateBannerColor(index) {
        const banner = this.element.closest('.banner-section');
        const slide = this.splide.root.querySelectorAll('.splide__slide')[index];
        const color = slide && slide.dataset.color;

        if (!banner || !color) {
            return;
        }

        banner.dataset.color = color;
    }

	updatePagination(newIndex) {
		const slider_pagination = Array.from(this.splide.root.querySelectorAll('.splide__custom__pagination li'));
		slider_pagination.forEach((item, index) => {
			const pie = item.querySelector('.pie');
			if (index === newIndex) {
				item.classList.add('actSlide');
				pie.style.strokeDasharray = '';
				pie.getBoundingClientRect();
				pie.style.strokeDasharray = strokeDasharray;
			} else {
				item.classList.remove('actSlide');
				pie.style.strokeDasharray = '';
			}
		});
	}
}
